import { watch } from 'vue';

import { IamClientUser } from '@evidentid/iam-client/types';
import IdleTimeTracker from '@evidentid/idle-time-tracker';

import { createModuleBuilder } from '../../app';
import { AuthModule } from '../auth';
import { useUserStore } from '../auth/store/user';
import { SnackbarModule } from '../snackbar';
import { useSnackbarStore } from '../snackbar/store';

export function createIdleLogoutModule() {
    return createModuleBuilder()
        .demandOptions<{ logoutAfterMs: number }>()
        .demand<AuthModule>()
        .demand<SnackbarModule>()
        .execute((app) => {
            const idleTimeTracker = new IdleTimeTracker({ id: '$idleLogoutTimestamp' });
            let idleLogoutTimeout: any = null;
            let previousUser: IamClientUser | null = null;

            async function idleLogoutTick(): Promise<void> {
                if (idleTimeTracker.get() > app.options.logoutAfterMs) {
                    await useUserStore(app.getPinia()).logOut();
                    useSnackbarStore(app.getPinia()).displaySnackbar({
                        message: 'You have been logged out due to inactivity.',
                        success: false,
                        permanent: true,
                    });
                    return;
                }
                // Schedule next check
                idleLogoutTimeout = setTimeout(idleLogoutTick, 1000);
            }

            async function startTracking(): Promise<void> {
                idleTimeTracker.start();
                await idleLogoutTick();
            }

            function stopTracking(): void {
                idleTimeTracker.stop();
                clearTimeout(idleLogoutTimeout);
            }

            async function onUserChange(user: IamClientUser | null): Promise<void> {
                if (!user) {
                    stopTracking();
                } else if (!previousUser) {
                    await startTracking();
                }
                previousUser = user;
            }
            const userStore = useUserStore(app.getPinia());
            watch(() => userStore.data, onUserChange);
            app.once('afterDestroy', stopTracking);
            onUserChange(userStore.data);
        })
        .end();
}

export type IdleLogoutModule = ReturnType<typeof createIdleLogoutModule>;
